import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { formatNumber } from "shared/utils/numberFormatter";
import { makeFormatValue, getThresholdValues } from "features/analytics/studio/utils/axisFormat";
import { formatDurationSeconds } from "features/analytics/studio/utils/durationFormat";

/**
 * Single funnel bar. Uses chart config (axis.leftY, colors) like Insights when config is provided.
 */
export default function FunnelSummaryBarChart({
  steps = [],
  config,
  measuring = "conversion_rate",
}) {
  const leftY = config?.axis?.leftY ?? {};
  const rightY = config?.axis?.rightY ?? {};
  const xAxis = config?.axis?.xAxis ?? {};
  const funnelAxis = config?.funnelAxis ?? "left";
  const yAxisConfig = funnelAxis === "right" ? rightY : leftY;
  const formatYValue = makeFormatValue(yAxisConfig);
  const { min: minThreshold, max: maxThreshold } = getThresholdValues(yAxisConfig);
  const leftYVisible = yAxisConfig.visible !== false;
  const leftYScale =
    yAxisConfig && String(yAxisConfig.scale) === "log" ? "log" : "linear";
  const leftYLabel = yAxisConfig.label;
  const outOfBoundsMode = yAxisConfig.outOfBounds ?? "hidden"; // "hidden" = cropped view; "visible" = full range
  const xAxisVisible = xAxis.visible !== false;
  const xAxisLabel = xAxis.label || "";
  const barColor = config?.colors?.[0] ?? theme.primaryLight;
  if (!Array.isArray(steps) || steps.length === 0) {
    return (
      <div className="flex items-center justify-center h-[240px]">
        <div className="text-sm" style={{ color: theme.textMuted }}>
          No data available
        </div>
      </div>
    );
  }

  const firstStep = steps[0];
  const lastStep = steps[steps.length - 1];

  const firstCount = Number(firstStep.count || 0);
  const lastCount = Number(lastStep.count || 0);

  if (measuring === "time_to_convert" && steps.length >= 2) {
    const seconds = Number(lastStep.value || 0);
    const completers = lastCount;
    const firstLabel = firstStep.label || firstStep.eventName || "step 1";
    const lastLabel =
      lastStep.label || lastStep.eventName || `step ${steps.length}`;
    const title = `Time to convert (${firstLabel} → ${lastLabel})`;
    const barLabel = "Time to convert";
    const autoMax = Math.max(seconds * 1.2, 1);
    const hasThresholds = minThreshold != null || maxThreshold != null;
    let xDomain;
    if (hasThresholds && outOfBoundsMode === "hidden") {
      const baseMin = leftYScale === "log" ? 0.01 : 0;
      const min = minThreshold != null ? minThreshold : baseMin;
      const max = maxThreshold != null ? maxThreshold : autoMax;
      const safeMin = leftYScale === "log" ? Math.max(0.01, min) : min;
      const safeMax =
        leftYScale === "log"
          ? Math.max(safeMin * 1.1, max)
          : Math.max(safeMin, max);
      xDomain = [safeMin, safeMax];
    } else {
      const baseMin = leftYScale === "log" ? 0.01 : 0;
      const safeMin = minThreshold != null ? minThreshold : baseMin;
      const safeMax = Math.max(autoMax, maxThreshold ?? autoMax);
      xDomain = [safeMin, safeMax];
    }

    const data = [
      {
        name: barLabel,
        value: seconds,
        users: completers,
        __actualValue: seconds,
      },
    ];

    const TooltipContentTtc = ({ active, payload }) => {
      if (!active || !payload || !payload.length) return null;
      const point = payload[0].payload || {};
      const value = point.__actualValue != null ? Number(point.__actualValue) : Number(point.value || 0);
      const users = Number(point.users || 0);
      return (
        <div
          style={{
            backgroundColor: "var(--surface-base)",
            borderRadius: 8,
            boxShadow: "0 4px 12px var(--border-default)",
            border: `1px solid ${theme.border}`,
            padding: "12px 16px",
            minWidth: 240,
            fontFamily: "Inter, sans-serif",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: theme.textPrimary,
              marginBottom: 4,
              lineHeight: 1.4,
            }}
          >
            {title}
          </div>
          <div style={{ fontSize: 13, color: theme.textPrimary, marginBottom: 2 }}>
            {formatDurationSeconds(value)}
          </div>
          <div style={{ fontSize: 13, color: theme.textPrimary, marginBottom: 4 }}>
            {formatNumber(users)} completers
          </div>
        </div>
      );
    };

    const xAxisLabelProps = leftYLabel
      ? {
          label: {
            value: leftYLabel,
            position: "insideBottom",
            offset: -8,
            style: { fontSize: 12, fill: "var(--text-muted)" },
          },
        }
      : {};

    return (
      <div className="w-full" style={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 16, right: 24, left: 24, bottom: 16 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--border-default)"
              horizontal={false}
            />
            <XAxis
              type="number"
              hide={!leftYVisible}
              domain={xDomain}
              scale={leftYScale}
              allowDataOverflow={hasThresholds && outOfBoundsMode === "hidden"}
              tick={{ fill: theme.textMuted, fontSize: 11 }}
              axisLine={{ stroke: "var(--border-default)" }}
              tickLine={false}
              tickFormatter={(v) => formatDurationSeconds(v)}
              {...xAxisLabelProps}
            />
            <YAxis
              type="category"
              dataKey="name"
              hide={!xAxisVisible}
              {...(xAxisLabel
                ? {
                    label: {
                      value: xAxisLabel,
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 12, fill: "var(--text-muted)" },
                    },
                  }
                : {})}
            />
            <Tooltip content={<TooltipContentTtc />} />
            <Bar
              dataKey="value"
              fill={barColor}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const firstLbl = firstStep.label || firstStep.eventName || "step 1";
  const lastLbl = lastStep.label || lastStep.eventName || `step ${steps.length}`;

  const overallConversionPct =
    typeof lastStep.overallConversion === "number"
      ? lastStep.overallConversion
      : typeof firstStep.overallConversion === "number"
        ? firstStep.overallConversion
        : firstCount > 0
          ? (lastCount / firstCount) * 100
          : 0;

  let barValue = 0;
  let barName = "Metric";
  let tooltipHeader = "";
  let tickFmt = (v) => formatYValue(v);
  let tooltipLine1 = "";
  let tooltipLine2 = null;
  let tooltipLine3 = null;

  switch (measuring) {
    case "unique_users": {
      const v = Number(
        lastStep.value !== undefined && lastStep.value !== ""
          ? lastStep.value
          : lastCount
      );
      barValue = Number.isFinite(v) ? v : 0;
      barName = "Unique users";
      tooltipHeader = `Unique users (${firstLbl} → ${lastLbl})`;
      tickFmt = (x) => formatNumber(x);
      tooltipLine1 = `${formatNumber(barValue)} unique users (last step)`;
      tooltipLine2 = `${formatNumber(firstCount)} entered funnel`;
      break;
    }
    case "total_conversions": {
      const v = Number(lastStep.value ?? lastCount ?? 0);
      barValue = Number.isFinite(v) ? v : 0;
      barName = "Total conversions";
      tooltipHeader = `Total conversions (${firstLbl} → ${lastLbl})`;
      tickFmt = (x) => formatNumber(x);
      tooltipLine1 = `${formatNumber(barValue)} total conversions`;
      break;
    }
    case "total_sessions": {
      const v = Number(lastStep.value ?? lastCount ?? 0);
      barValue = Number.isFinite(v) ? v : 0;
      barName = "Sessions";
      tooltipHeader = `Sessions (${firstLbl} → ${lastLbl})`;
      tickFmt = (x) => formatNumber(x);
      tooltipLine1 = `${formatNumber(barValue)} sessions`;
      break;
    }
    case "property_sum": {
      const v = Number(lastStep.value ?? 0);
      barValue = Number.isFinite(v) ? v : 0;
      barName = "Property sum";
      tooltipHeader = `Property sum (${firstLbl} → ${lastLbl})`;
      tickFmt = (x) => formatYValue(x);
      tooltipLine1 = formatYValue(barValue);
      break;
    }
    case "conversion_rate":
    default: {
      barValue = overallConversionPct;
      barName = "Conversion rate";
      tooltipHeader = `Conversion Rate of ${firstLbl} through ${lastLbl} \u2013 All Steps`;
      tickFmt = (v) => `${Number(v).toFixed(0)}%`;
      tooltipLine1 = `${overallConversionPct.toFixed(1)}% converted`;
      tooltipLine2 = `${formatNumber(lastCount)} users completed last step`;
      tooltipLine3 = `${formatNumber(firstCount)} users at first step`;
      break;
    }
  }

  const hasThresholds = minThreshold != null || maxThreshold != null;
  const autoMax =
    measuring === "conversion_rate" || measuring === undefined
      ? Math.max(100, barValue * 1.2)
      : Math.max(barValue * 1.2, 1);

  let xDomain;
  if (hasThresholds && outOfBoundsMode === "hidden") {
    const baseMin = leftYScale === "log" ? 0.01 : 0;
    const min = minThreshold != null ? minThreshold : baseMin;
    const max = maxThreshold != null ? maxThreshold : autoMax;
    const safeMin = leftYScale === "log" ? Math.max(0.01, min) : min;
    const safeMax =
      leftYScale === "log" ? Math.max(safeMin * 1.1, max) : Math.max(safeMin, max);
    xDomain = [safeMin, safeMax];
  } else {
    const baseMin = leftYScale === "log" ? 0.01 : 0;
    const safeMin = minThreshold != null ? minThreshold : baseMin;
    const safeMax = Math.max(autoMax, maxThreshold ?? autoMax);
    xDomain = [safeMin, safeMax];
  }

  const xAxisLabelProps = leftYLabel ? { label: { value: leftYLabel, position: "insideBottom", offset: -8, style: { fontSize: 12, fill: "var(--text-muted)" } } } : {};

  const data = [
    {
      name: barName,
      value: barValue,
      users: lastCount,
      __actualValue: barValue,
    },
  ];

  const TooltipContent = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div
        style={{
          backgroundColor: "var(--surface-base)",
          borderRadius: 8,
          boxShadow: "0 4px 12px var(--border-default)",
          border: `1px solid ${theme.border}`,
          padding: "12px 16px",
          minWidth: 240,
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: theme.textPrimary,
            marginBottom: 4,
            lineHeight: 1.4,
          }}
        >
          {tooltipHeader}
        </div>
        <div
          style={{
            fontSize: 13,
            color: theme.textPrimary,
            marginBottom: 2,
          }}
        >
          {tooltipLine1}
        </div>
        {tooltipLine2 && (
          <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 2 }}>{tooltipLine2}</div>
        )}
        {tooltipLine3 && (
          <div style={{ fontSize: 12, color: theme.textMuted }}>{tooltipLine3}</div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full" style={{ height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 16, right: 24, left: 24, bottom: 16 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border-default)"
            horizontal={false}
          />
          <XAxis
            type="number"
            hide={!leftYVisible}
            domain={xDomain}
            scale={leftYScale}
            // Hidden + thresholds => enforce [Min, Max] and crop bar; otherwise let Recharts extend to fit
            allowDataOverflow={hasThresholds && outOfBoundsMode === "hidden"}
            tick={{ fill: theme.textMuted, fontSize: 11 }}
            axisLine={{ stroke: "var(--border-default)" }}
            tickLine={false}
            tickFormatter={tickFmt}
            {...xAxisLabelProps}
          />
          <YAxis
            type="category"
            dataKey="name"
            hide={!xAxisVisible}
            {...(xAxisLabel ? {
              label: {
                value: xAxisLabel,
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 12, fill: "var(--text-muted)" }
              }
            } : {})}
          />
          <Tooltip content={<TooltipContent />} />
          <Bar
            dataKey="value"
            fill={barColor}
            radius={[0, 4, 4, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

